import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { buildLocalizedPath } from '@/utils/localeSlug';

// Hooks
import useResponsive from '@/hooks/useResponsive';

// Other
import { type FooterType } from '@/services/footer/footerService';
import EmailIcon from '@/assets/icons/email.svg?react';
import PhoneIcon from '@/assets/icons/phone.svg?react';
import PositionIcon from '@/assets/icons/position.svg?react';
import HoursIcon from '@/assets/icons/hours.svg?react';
import ShapesIcon from '@/assets/icons/shapes.svg?react';

// Styles
import styles from './Footer.module.css';

interface FooterProps {
  footer: FooterType,
}

function Footer({
  footer
}: FooterProps) {
  const { isMobile } = useResponsive();
  const { getLng, localizedPath } = useI18n();

  if (!footer) {
    return null;
  }

  const { contact, company, support, legal, logo } = footer;

  return (
    <footer className={styles.bg}>
      <div className={styles.container}>
        <nav className={styles.nav}>
          <div>
            <h4 className={styles.title}>
              {contact.title}
            </h4>
            <div className={styles.links}>
              <Link to={`mailto:${contact.email}`} className={styles.link}>
                <EmailIcon />
                {contact.email}
              </Link>
              {contact.phone?.trim() ? (
                <Link to={`tel:${contact.phone}`} className={styles.link}>
                  <PhoneIcon />
                  {contact.phone}
                </Link>
              ) : null}
              {contact.address?.trim() ? (
                <div className={styles.text}>
                  <PositionIcon />
                  {contact.address}
                </div>
              ) : null}
              <div className={styles.text}>
                <HoursIcon />
                {contact.hours}
              </div>

            </div>
          </div>
          <div>
            <h4 className={styles.title}>
              {company.title}
            </h4>
            <div className={styles.links}>
              {
                company.links.map((link) => (
                  <Link key={`${company.id}-${link.title}`} to={localizedPath(link.url)} className={styles.link}>
                    { link.icon ? (<img src={link.icon?.url} alt={link.icon?.alternativeText || ''} />) : null }
                    {link.title}
                  </Link>

                ))
              }
            </div>
          </div>
          {
            !isMobile ? (
              <>
                <div>
                  <h4 className={styles.title}>
                    {support.title}
                  </h4>
                  <div className={styles.links}>
                    {
                      support.links.map((link) => (
                        <Link
                          key={`${support.id}-${link.title}`}
                          to={localizedPath(link.url)}
                          className={styles.link}
                        >
                          { link.icon ? (<img src={link.icon?.url} alt={link.icon?.alternativeText || ''} />) : null }
                          {link.title}
                        </Link>

                      ))
                    }
                  </div>
                </div>
                <div className={styles['logo-column']}>
                  <div>
                    <h4 className={styles.title}>
                      {legal.title}
                    </h4>
                    <div className={styles.links}>
                      {
                        legal.links.map((link) => (
                          <Link
                            key={`${legal.id}-${link.title}`}
                            to={localizedPath(link.url)}
                            className={styles.link}
                          >
                            { link.icon ? (<img src={link.icon?.url} alt={link.icon?.alternativeText || ''} />) : null }
                            {link.title}
                          </Link>

                        ))
                      }
                    </div>
                  </div>
                  <Link to={buildLocalizedPath('', getLng())}>
                    <img src={logo.url} alt={logo.alternativeText || ''} />
                  </Link>
                </div>
              </>
            ) : null
          }
        </nav>
        {
          isMobile ? (
            <div className={styles['legal-responsive']}>
              {
                legal.links.map((link) => (
                  <Link
                    key={`${legal.id}-${link.title}`}
                    to={localizedPath(link.url)}
                    className={styles['legal-responsive-item']}
                  >
                    { link.icon ? (<img src={link.icon?.url} alt={link.icon?.alternativeText || ''} />) : null }
                    {link.title}
                  </Link>

                ))
              }
            </div>
          ) : null
        }
      </div>
      <ShapesIcon className={styles['footer-shapes']} />
    </footer>
  );
}

export default Footer;